<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { buildFilterList, getFilterType } from './helpers/SLAFilterHelpers';
import {
  parseFilterURLParams,
  generateCompleteURLParams,
} from '../../helpers/reportFilterHelper';
import SLAFilterModal from './SLAFilterModal.vue';

const emit = defineEmits(['filterChange']);

const DEFAULT_FILTERS = {
  assigned_agent_id: [],
  inbox_id: [],
  team_id: [],
  sla_policy_id: [],
  label_list: [],
};

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const showFilterModal = ref(false);
const appliedFilters = ref({ ...DEFAULT_FILTERS });

const agents = computed(() => store.getters['agents/getAgents']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const teams = computed(() => store.getters['teams/getTeams']);
const labels = computed(() => store.getters['labels/getLabels']);
const sla = computed(() => store.getters['sla/getSLA']);

const selectedIdsMap = computed(() => {
  return Object.entries(appliedFilters.value).reduce((map, [key, value]) => {
    map[getFilterType(key, 'keyToType')] = value;
    return map;
  }, {});
});

const modalFilters = computed(() => {
  const filterTypes = [
    {
      id: '1',
      name: t('SLA_REPORTS.FILTERS.SLA'),
      type: 'sla',
    },
    {
      id: '2',
      name: t('SLA_REPORTS.FILTERS.INBOXES'),
      type: 'inboxes',
    },
    {
      id: '3',
      name: t('SLA_REPORTS.FILTERS.AGENTS'),
      type: 'agents',
    },
    {
      id: '4',
      name: t('SLA_REPORTS.FILTERS.TEAMS'),
      type: 'teams',
    },
    {
      id: '5',
      name: t('SLA_REPORTS.FILTERS.LABELS'),
      type: 'labels',
    },
  ];

  const sources = {
    agents: agents.value,
    inboxes: inboxes.value,
    teams: teams.value,
    labels: labels.value,
    sla: sla.value,
  };

  return filterTypes.map(filter => ({
    ...filter,
    options: buildFilterList(sources[filter.type], filter.type),
    selectedIds: selectedIdsMap.value[filter.type] || [],
  }));
});

const activeFilterCount = computed(() => {
  return Object.values(appliedFilters.value).reduce(
    (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
    0
  );
});

const hasActiveFilters = computed(() => activeFilterCount.value > 0);

const updateURLParams = () => {
  const params = generateCompleteURLParams({
    from: route.query.from,
    to: route.query.to,
    range: route.query.range,
    filters: {
      agent_id: appliedFilters.value.assigned_agent_id,
      inbox_id: appliedFilters.value.inbox_id,
      team_id: appliedFilters.value.team_id,
      sla_policy_id: appliedFilters.value.sla_policy_id,
      label: appliedFilters.value.label_list,
    },
  });
  router.replace({ query: params });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', { ...appliedFilters.value });
};

const addFilter = item => {
  const { type, id } = item;
  const key = getFilterType(type, 'typeToKey');
  const current = appliedFilters.value[key] || [];

  appliedFilters.value[key] = current.includes(id)
    ? current.filter(v => v !== id)
    : [...current, id];

  emitChange();
};

const clearGroup = type => {
  const key = getFilterType(type, 'typeToKey');
  appliedFilters.value[key] = [];
  emitChange();
};

const selectGroup = type => {
  const key = getFilterType(type, 'typeToKey');
  const sources = {
    agents: agents.value,
    inboxes: inboxes.value,
    teams: teams.value,
    labels: labels.value,
    sla: sla.value,
  };
  const options = buildFilterList(sources[type], type);
  appliedFilters.value[key] = options.map(opt => opt.id);
  emitChange();
};

const clearAllFilters = () => {
  appliedFilters.value = { ...DEFAULT_FILTERS };
  emitChange();
};

const initializeFromURL = () => {
  const urlFilters = parseFilterURLParams(route.query);
  appliedFilters.value.assigned_agent_id = urlFilters.agent_id || [];
  appliedFilters.value.inbox_id = urlFilters.inbox_id || [];
  appliedFilters.value.team_id = urlFilters.team_id || [];
  appliedFilters.value.sla_policy_id = urlFilters.sla_policy_id || [];
  appliedFilters.value.label_list = urlFilters.label || [];
};

onMounted(() => {
  initializeFromURL();
  if (hasActiveFilters.value) {
    emitChange();
  }
});
</script>

<template>
  <div class="flex items-center gap-2">
    <button
      type="button"
      class="relative inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-n-slate-7 bg-n-alpha-1 hover:border-n-slate-9"
      @click="showFilterModal = true"
    >
      {{ $t('SLA_REPORTS.FILTERS.ADD_FILTER') }}

      <span
        v-if="activeFilterCount > 0"
        class="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[11px] font-semibold rounded-full bg-woot-500 text-white"
      >
        {{ activeFilterCount }}
      </span>
    </button>

    <button
      v-if="hasActiveFilters"
      type="button"
      class="px-3 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 hover:underline"
      @click="clearAllFilters"
    >
      {{ $t('SLA_REPORTS.FILTERS.CLEAR_ALL') }}
    </button>

    <SLAFilterModal
      :show="showFilterModal"
      :filters="modalFilters"
      @close="showFilterModal = false"
      @add-filter="addFilter"
      @clear-group="clearGroup"
      @select-group="selectGroup"
    />
  </div>
</template>
